import * as core from '@actions/core';
import {
  parseBoolean,
  parseList,
  parseNonNegativeNumber,
  parsePositiveInteger,
} from './utils';

export type PassKind = 'standard' | 'adversarial';

export interface ActionConfig {
  githubToken: string;
  model: string;
  sandbox: string;
  passes: PassKind[];
  parallelPasses: boolean;
  policyPaths: string[];
  standardPromptPath: string;
  adversarialPromptPath: string;
  extraInstructions: string;
  includeRebuttal: boolean;
  postComments: boolean;
  autoMerge: boolean;
  failOnBlock: boolean;
  prNumber: number | null;
  reviewDir: string;
  outputDir: string | null;
  maxSnapshotBytes: number;
  maxTotalSnapshotBytes: number;
  maxDiffBytes: number;
  maxDiagnosticBytes: number;
  maxReviewMetadataBytes: number;
  maxReviewOutputBytes: number;
  maxGitRepositoryBytes: number;
  maxAttempts: number;
  initialBackoffSeconds: number;
  safePath: string;
  workspace: string;
}

export function readConfig(): ActionConfig {
  const passes = parsePasses(core.getInput('passes'));
  const outputInput = core.getInput('output-dir').trim();

  const prNumberInput = core.getInput('pr-number');
  const prNumber = prNumberInput.trim() === '' ? null : parsePositiveInteger(prNumberInput, 'pr-number');

  const sandbox = core.getInput('sandbox') || 'read-only';
  const parallelPasses = parseBoolean(core.getInput('parallel-passes'), false);
  if (parallelPasses && sandbox !== 'read-only') {
    throw new Error('parallel-passes requires sandbox=read-only so concurrent reviews cannot mutate a shared workspace.');
  }

  return {
    githubToken: requiredInput('github-token'),
    model: core.getInput('model') || 'gpt-5.6-luna',
    sandbox,
    passes,
    parallelPasses,
    policyPaths: parseList(core.getInput('policy-paths')),
    standardPromptPath: core.getInput('standard-prompt-path') || '.github/codex-review/standard.md',
    adversarialPromptPath: core.getInput('adversarial-prompt-path') || '.github/codex-review/adversarial.md',
    extraInstructions: core.getInput('extra-instructions'),
    includeRebuttal: parseBoolean(core.getInput('include-pr-comments'), true),
    postComments: parseBoolean(core.getInput('post-comments'), true),
    autoMerge: parseBoolean(core.getInput('auto-merge'), false),
    failOnBlock: parseBoolean(core.getInput('fail-on-block'), true),
    prNumber,
    reviewDir: core.getInput('review-dir') || '.codex-review',
    outputDir: outputInput === '' ? null : outputInput,
    maxSnapshotBytes: parsePositiveInteger(core.getInput('max-snapshot-bytes') || '1048576', 'max-snapshot-bytes'),
    maxTotalSnapshotBytes: parsePositiveInteger(core.getInput('max-total-snapshot-bytes') || '52428800', 'max-total-snapshot-bytes'),
    maxDiffBytes: parsePositiveInteger(core.getInput('max-diff-bytes') || '52428800', 'max-diff-bytes'),
    maxDiagnosticBytes: parsePositiveInteger(core.getInput('max-diagnostic-bytes') || '52428800', 'max-diagnostic-bytes'),
    maxReviewMetadataBytes: parsePositiveInteger(core.getInput('max-review-metadata-bytes') || '16777216', 'max-review-metadata-bytes'),
    maxReviewOutputBytes: parsePositiveInteger(core.getInput('max-review-output-bytes') || '1048576', 'max-review-output-bytes'),
    maxGitRepositoryBytes: parsePositiveInteger(core.getInput('max-git-repository-bytes') || '1073741824', 'max-git-repository-bytes'),
    maxAttempts: parsePositiveInteger(core.getInput('max-attempts') || '3', 'max-attempts'),
    initialBackoffSeconds: parseNonNegativeNumber(core.getInput('initial-backoff-seconds') || '15', 'initial-backoff-seconds'),
    safePath: core.getInput('safe-path') || process.env.PATH || '/usr/local/bin:/usr/bin:/bin',
    workspace: process.env.GITHUB_WORKSPACE || process.cwd(),
  };
}

function requiredInput(name: string): string {
  const value = core.getInput(name);
  if (value.trim() === '') {
    throw new Error(`Missing required input: ${name}`);
  }
  return value;
}

function parsePasses(input: string): PassKind[] {
  const values = parseList(input || 'standard,adversarial');
  const seen = new Set<PassKind>();
  for (const value of values) {
    if (value !== 'standard' && value !== 'adversarial') {
      throw new Error(`Unsupported review pass: ${value}`);
    }
    seen.add(value);
  }
  if (seen.size === 0) {
    throw new Error('At least one review pass is required.');
  }
  return [...seen];
}
